import { useState, useCallback, useEffect } from 'react';
import QRCode from 'qrcode';
import Swal from 'sweetalert2';
import { defaultAdminQrData, type RadioQrData } from '@/mocks/radioQr';

export default function SettingsQRTab() {
  const [qrData] = useState<RadioQrData>(defaultAdminQrData);
  const [showDestEditor, setShowDestEditor] = useState(false);
  const [destType, setDestType] = useState<'custom_domain' | 'rfe_page'>(qrData.currentDestination.type);
  const [customDomainInput, setCustomDomainInput] = useState(qrData.customDomain || '');
  const [qrPreviewUrl, setQrPreviewUrl] = useState('');
  const [qrLoading, setQrLoading] = useState(true);

  // ═══════════════════════════════════════════
  // SINGLE SOURCE OF TRUTH — permanent_url
  // ═══════════════════════════════════════════
  const permanentUrl = `https://${qrData.permanentUrl}`;

  // Generate real QR code for preview
  useEffect(() => {
    let cancelled = false;
    setQrLoading(true);

    QRCode.toDataURL(permanentUrl, {
      errorCorrectionLevel: 'H',
      width: 420,
      margin: 2,
      color: { dark: '#0f172a', light: '#ffffff' },
    })
      .then((url) => {
        if (!cancelled) {
          setQrPreviewUrl(url);
          setQrLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setQrLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [permanentUrl]);

  // Copy — uses permanentUrl
  const handleCopyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(permanentUrl);
      Swal.fire({
        title: 'URL copiada',
        html: `<p style="font-size:13px;color:#6b7280"><code style="background:#f3f4f6;padding:2px 6px;border-radius:4px;font-size:12px">${permanentUrl}</code></p>`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', htmlContainer: 'text-sm' },
      });
    } catch {
      Swal.fire({
        title: 'Copiado manual',
        html: `<p style="font-size:12px;word-break:break-all;color:#374151">${permanentUrl}</p>`,
        icon: 'info',
        customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm' },
        buttonsStyling: false,
        confirmButtonText: 'Cerrar',
      });
    }
  }, [permanentUrl]);

  // Download — generates real QR PNG 2048×2048 with logo + validates payload
  const handleDownloadQr = useCallback(async () => {
    const size = 2048;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    // Step 1: Generate QR on a temp canvas at full resolution
    const qrTempCanvas = document.createElement('canvas');
    try {
      await QRCode.toCanvas(qrTempCanvas, permanentUrl, {
        errorCorrectionLevel: 'H',
        width: size,
        margin: 4,
        color: { dark: '#0f172a', light: '#ffffff' },
      });
    } catch (err) {
      Swal.fire({ title: 'Error', text: 'No se pudo generar el QR.', icon: 'error', customClass: { popup: 'rounded-2xl' } });
      return;
    }
    ctx.drawImage(qrTempCanvas, 0, 0, size, size);

    // Step 2: Overlay logo in center
    const logoSize = Math.floor(size * 0.18);
    const logoX = (size - logoSize) / 2;
    const logoY = (size - logoSize) / 2;
    const logoPad = Math.floor(size * 0.008);

    try {
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        logoImg.onload = () => resolve();
        logoImg.onerror = () => resolve();
        logoImg.src = qrData.logoUrl;
      });
      if (logoImg.width > 0 && logoImg.height > 0) {
        // White rounded-rect background behind logo for clean separation
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.roundRect(logoX - logoPad, logoY - logoPad, logoSize + logoPad * 2, logoSize + logoPad * 2, logoPad * 2);
        ctx.fill();
        ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
      }
    } catch {
      // Logo failed to load — download QR without logo, still functional
    }

    // Step 3: Export as PNG and trigger download
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/png');
    });

    if (!blob) {
      Swal.fire({ title: 'Error', text: 'No se pudo generar la imagen.', icon: 'error', customClass: { popup: 'rounded-2xl' } });
      return;
    }

    const sanitizedSlug = qrData.redirectCode.replace(/[^a-zA-Z0-9_-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    const filename = `qr-radio-${sanitizedSlug}.png`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    Swal.fire({
      title: 'QR Descargado',
      html: `<p style="font-size:13px"><code style="background:#f3f4f6;padding:2px 6px;border-radius:4px;font-size:12px">${filename}</code></p><p style="font-size:11px;color:#6b7280;margin-top:4px">2048 × 2048 px — PNG</p><p style="font-size:10px;color:#9ca3af;margin-top:2px">Codifica: ${permanentUrl}</p>`,
      icon: 'success',
      timer: 3000,
      showConfirmButton: false,
      customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', htmlContainer: 'text-sm' },
    });
  }, [permanentUrl, qrData]);

  const handleUpdateDestination = useCallback(() => {
    const label =
      destType === 'custom_domain'
        ? customDomainInput || 'dominio-personalizado.com'
        : 'Perfil en RadioFrecuenciaWeb';
    const url =
      destType === 'custom_domain'
        ? `https://${customDomainInput || 'dominio-personalizado.com'}`
        : `/r/${qrData.redirectCode}`;
    Swal.fire({
      title: 'Destino actualizado',
      html: `<div style="text-align:left;font-size:13px"><p style="margin-bottom:8px">El QR <strong>${permanentUrl}</strong> ahora redirige a:</p><div style="background:#f3f4f6;padding:10px;border-radius:8px"><p style="font-weight:600;color:#111827;margin:0">${label}</p><p style="font-size:11px;color:#6b7280;margin:4px 0 0">${url}</p></div><p style="font-size:10px;color:#9ca3af;margin-top:8px"><i class="ri-information-line"></i> La URL permanente <strong>NO</strong> cambia. Solo cambia el destino.</p></div>`,
      icon: 'success',
      confirmButtonText: 'Entendido',
      customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50', htmlContainer: 'text-sm' },
      buttonsStyling: false,
    });
    setShowDestEditor(false);
  }, [destType, customDomainInput, qrData, permanentUrl]);

  return (
    <div className="space-y-6">
      {/* Permanent URL notice */}
      <div className="bg-accent-50 border border-accent-200 rounded-2xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shrink-0 mt-0.5">
          <i className="ri-information-line text-accent-500 text-sm"></i>
        </div>
        <div>
          <p className="text-accent-800 text-xs font-bold mb-0.5">URL Permanente</p>
          <p className="text-accent-600/80 text-[11px] leading-relaxed">
            Este QR nunca apunta directo a tu frecuencia ni dominio. Siempre usa{' '}
            <strong>{permanentUrl}</strong> — una URL que no cambia.
          </p>
        </div>
      </div>

      {/* QR Card */}
      <div className="bg-background-50 border border-background-200 rounded-2xl p-6">
        <div className="flex flex-col lg:flex-row gap-8 items-center">
          {/* QR Preview — real QR code, not a fake image */}
          <div className="shrink-0">
            <div className="w-56 h-56 rounded-2xl bg-white border border-background-200 p-4 flex items-center justify-center overflow-hidden">
              {qrLoading && !qrPreviewUrl ? (
                <div className="flex flex-col items-center gap-2">
                  <i className="ri-loader-4-line animate-spin text-foreground-300 text-xl"></i>
                  <span className="text-foreground-400 text-[9px]">Generando QR...</span>
                </div>
              ) : qrPreviewUrl ? (
                <img
                  src={qrPreviewUrl}
                  alt={`QR: ${permanentUrl}`}
                  title={`QR: ${permanentUrl}`}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <i className="ri-error-warning-line text-foreground-300 text-xl"></i>
                  <span className="text-foreground-400 text-[9px]">No se pudo generar el QR</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0 space-y-4 w-full">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-background-100 overflow-hidden shrink-0">
                <img src={qrData.logoUrl} alt={qrData.radioName} className="w-full h-full object-cover" />
              </div>
              <div className="min-w-0">
                <p className="text-foreground-800 text-sm font-bold truncate">{qrData.radioName}</p>
                <p className="text-foreground-400 text-[11px]">{qrData.frequency} MHz</p>
              </div>
            </div>

            <div>
              <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1.5">
                URL Permanente del QR
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-background-100 border border-background-200 rounded-xl px-4 py-2.5 text-foreground-800 text-sm font-mono break-all">
                  {permanentUrl}
                </code>
                <button
                  onClick={handleCopyUrl}
                  className="shrink-0 w-10 h-10 rounded-xl bg-foreground-950 text-background-50 flex items-center justify-center cursor-pointer hover:bg-foreground-800 transition-all active:scale-95 whitespace-nowrap"
                  title="Copiar URL"
                >
                  <i className="ri-file-copy-line text-sm"></i>
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleCopyUrl}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-copy-line text-xs"></i>
                Copiar URL
              </button>
              <button
                onClick={handleDownloadQr}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-background-100 border border-background-200 text-foreground-700 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-line text-xs"></i>
                Descargar QR
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Current destination card */}
      <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-foreground-950 font-bold text-sm">Destino Actual</h3>
            <p className="text-foreground-400 text-[10px] mt-0.5">
              A dónde redirige el QR cuando alguien lo escanea
            </p>
          </div>
          <button
            onClick={() => {
              setDestType(qrData.currentDestination.type);
              setCustomDomainInput(qrData.customDomain || '');
              setShowDestEditor(!showDestEditor);
            }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line text-xs"></i>Cambiar destino
          </button>
        </div>
        <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100">
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
              qrData.currentDestination.type === 'custom_domain' ? 'bg-emerald-100' : 'bg-secondary-100'
            }`}
          >
            <i
              className={`text-sm ${
                qrData.currentDestination.type === 'custom_domain'
                  ? 'ri-global-line text-emerald-600'
                  : 'ri-radio-line text-secondary-600'
              }`}
            ></i>
          </div>
          <div className="min-w-0">
            <p className="text-foreground-800 text-xs font-bold">{qrData.currentDestination.label}</p>
            <p className="text-foreground-400 text-[11px] truncate">
              {qrData.currentDestination.url}
            </p>
          </div>
        </div>
        {showDestEditor && (
          <div className="mt-4 p-4 rounded-xl bg-background-100 border border-background-200 space-y-3">
            <p className="text-foreground-700 text-xs font-bold">Editar destino de redirección</p>
            <div className="flex gap-2">
              <button
                onClick={() => setDestType('rfe_page')}
                className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                  destType === 'rfe_page'
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-white border border-background-200 text-foreground-500 hover:border-foreground-300'
                }`}
              >
                <i className="ri-radio-line text-xs"></i>Perfil RFW
              </button>
              <button
                onClick={() => setDestType('custom_domain')}
                className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                  destType === 'custom_domain'
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-white border border-background-200 text-foreground-500 hover:border-foreground-300'
                }`}
              >
                <i className="ri-global-line text-xs"></i>Dominio propio
              </button>
            </div>
            {destType === 'custom_domain' && (
              <div>
                <label className="block text-foreground-400 text-[10px] font-semibold mb-1">
                  Dominio (sin https://)
                </label>
                <input
                  type="text"
                  value={customDomainInput}
                  onChange={(e) => setCustomDomainInput(e.target.value)}
                  placeholder="miradio.com"
                  className="w-full px-3 py-2 bg-white border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors"
                />
              </div>
            )}
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={handleUpdateDestination}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-check-line text-xs"></i>Guardar destino
              </button>
              <button
                onClick={() => setShowDestEditor(false)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white border border-background-200 text-foreground-500 text-xs font-medium hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* How it works */}
      <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
        <h3 className="text-foreground-950 font-bold text-sm mb-4">Cómo funciona</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            {
              step: '1',
              title: 'QR siempre apunta a:',
              desc: (
                <code className="bg-background-100 px-1.5 py-0.5 rounded text-[10px]">
                  {permanentUrl}
                </code>
              ),
            },
            {
              step: '2',
              title: 'El sistema decide:',
              desc:
                'Si tenés dominio propio activo, redirige allá. Si no, redirige a tu perfil en RadioFrecuenciaWeb.',
            },
            {
              step: '3',
              title: 'Nunca cambia:',
              desc:
                'Cambiá de plan, dominio o frecuencia. El QR siempre funciona igual.',
            },
          ].map((item) => (
            <div key={item.step} className="flex gap-3">
              <div className="w-8 h-8 rounded-lg bg-foreground-950 flex items-center justify-center shrink-0">
                <span className="text-background-50 text-[10px] font-bold">{item.step}</span>
              </div>
              <div>
                <p className="text-foreground-800 text-xs font-bold mb-0.5">{item.title}</p>
                <p className="text-foreground-400 text-[11px] leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}